import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  Paper,
  Select,
  MenuItem,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  useTheme,
  alpha,
  TextField,
  Link,
  Checkbox,
  ListItemText,
  Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Search as SearchIcon, ClearAll, Warning } from '@mui/icons-material';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import DownloadDetails from './DownloadDetails';
import UploadedFilesDetails from './UploadedFilesDetails';

// Document interface
interface Document {
  BidDocsID: number;
  DocumentName: string;
  DocumentType: string;
  DocumentDescription: string;
  InsertDate: string;
  DocumentUrl: string;
}

interface BidDocument {
  BidDocsID: number;
  BidID: number;
  DocumentName: string;
  DocumentUrl: string;
  Active: boolean;
  InsertDate: string;
  UpdateDate: string | null;
  DeleteDate: string | null;
  RowStatus: number;
  TownshipID: number;
  InsertedBy: number;
  UpdatedBy: number | null;
  DeletedBy: number | null;
  DocumentType: string;
  DocumentDescription: string;
}

// Updated Bid interface to match API response
interface Bid {
  bidId: string;
  title: string;
  type: string;
  status: string;
  category: string;
  uploads: number;
  downloads: string;
  lastModified: string;
  modifiedBy: string;
  contractNo: string;
  goLiveDate: string | null;
  bidDeadlineDate: string;
  awardDate: string | null;
  createDate: string;
  updateDate: string;
  createdBy: {
    UserID: number;
    EmailID: string;
  };
  updatedBy: {
    UserID: number;
    EmailID: string;
  };
  documents?: Document[]; // Add documents array
  childBidDocuments: BidDocument[];
}

// Updated SearchCriteria interface with date ranges
interface SearchCriteria {
  categories: number[];
  statuses: number[];
  bidTypes: number[];
  bidDeadlineFrom: Date | null;
  bidDeadlineTo: Date | null;
  awardDateFrom: Date | null;
  awardDateTo: Date | null;
  createdDateFrom: Date | null;
  createdDateTo: Date | null;
  modifiedBy: string;
  modifiedDate: Date | null;
  title: string;
}

interface BidResponse {
  BidID: number;
  Title: string;
  TypeID: number;
  StatusID: number;
  ContractNo: string;
  GoLiveDate: string | null;
  BidDeadLineDate: string;
  AwardDate: string | null;
  Status: string;
  Description: string;
  AdminNote: string;
  ReasonToReopen: string | null;
  AwardedTo: string | null;
  PONumber: string | null;
  ReasonToReject: string | null;
  Type: string;
  CreateDate: string;
  UpdateDate: string;
  CreatedBy: {
    UserID: number;
    EmailID: string;
  };
  UpdatedBy: {
    UserID: number;
    EmailID: string;
  };
  AwardedToVendor: string | null;
  AwardedToContact: string | null;
  Categories: {
    CategoryID: number;
    CategoryName: string;
  }[];
  Documents: Document[];
  Downloads: string;
  ChildBidDocuments: [];
}

interface APIResponse {
  status: string;
  message: string;
  data: {
    TotalRecords: number;
    CurrentPage: number;
    TotalPages: number;
    Data: BidResponse[];
  };
}

interface TypeOption {
  TypeID: number;
  Type: string;
  Active: boolean;
}

interface CategoryOption {
  CategoryID: number;
  CategoryName: string;
}

interface StatusOption {
  StatusID: number;
  Status: string;
  Active: boolean;
}

// Shared interface for bid details
interface BidDetails {
  contractNo: string;
  title: string;
  categories: string;
  documents?: Document[];
  childBidDocuments?: BidDocument[];
}

// Add interface for download details view
interface DownloadDetailsState {
  isOpen: boolean;
  bidId: string;
  bidDetails: BidDetails;
}

// Add interface for uploaded files details view
interface UploadedFilesDetailsState {
  isOpen: boolean;
  bidId: string;
  bidDetails: BidDetails;
}

const BidSummary: React.FC = () => {
  const theme = useTheme();

  // Updated searchCriteria state with date ranges
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>({
    categories: [],
    statuses: [],
    bidTypes: [],
    bidDeadlineFrom: null,
    bidDeadlineTo: null,
    awardDateFrom: null,
    awardDateTo: null,
    createdDateFrom: null,
    createdDateTo: null,
    modifiedBy: '',
    modifiedDate: null,
    title: ''
  });

  const [bids, setBids] = useState<Bid[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  // State for dropdown data
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [types, setTypes] = useState<TypeOption[]>([]);
  const [statuses, setStatuses] = useState<StatusOption[]>([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState(false);
  const [dropdownError, setDropdownError] = useState<string | null>(null);
  const [openCategorySelect, setOpenCategorySelect] = useState(false);
  const [openStatusSelect, setOpenStatusSelect] = useState(false);
  const [openTypeSelect, setOpenTypeSelect] = useState(false);

  // Add state for download details view
  const [downloadDetailsState, setDownloadDetailsState] =
    useState<DownloadDetailsState>({
      isOpen: false,
      bidId: '',
      bidDetails: {
        contractNo: '',
        title: '',
        categories: ''
      }
    });

  // Add state for uploaded files details view
  const [uploadedFilesDetailsState, setUploadedFilesDetailsState] =
    useState<UploadedFilesDetailsState>({
      isOpen: false,
      bidId: '',
      bidDetails: {
        contractNo: '',
        title: '',
        categories: ''
      }
    });

  // Helper function to scroll to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Fetch dropdown data (categories, types, statuses)
  useEffect(() => {
    const fetchDropdownData = async () => {
      setLoadingDropdowns(true);
      setDropdownError(null);
      try {
        // Fetch categories
        const categoriesResponse = await httpRequest(
          'GET',
          `/bid_portal/category/get_all`,
          {},
          {}
        );

        if (categoriesResponse.status === 'success') {
          const categoriesData =
            categoriesResponse.data?.data ||
            categoriesResponse.data?.Data ||
            [];
          setCategories(categoriesData);
        } else {
          console.error('Failed to fetch categories:', categoriesResponse);
          setDropdownError('Failed to load categories. Please try again.');
        }

        // Fetch types
        const typesResponse = await httpRequest(
          'GET',
          `/bid_portal/type/get_all`,
          {},
          {}
        );

        if (typesResponse.status === 'success') {
          const typesData =
            typesResponse.data?.data || typesResponse.data?.Data || [];
          setTypes(typesData);
        } else {
          console.error('Failed to fetch types:', typesResponse);
          setDropdownError('Failed to load bid types. Please try again.');
        }

        // Fetch statuses
        const statusesResponse = await httpRequest(
          'GET',
          `/bid_portal/status/get_all`,
          {},
          {}
        );

        if (statusesResponse.status === 'success') {
          const statusesData =
            statusesResponse.data?.data || statusesResponse.data?.Data || [];
          setStatuses(statusesData);
        } else {
          console.error('Failed to fetch statuses:', statusesResponse);
          setDropdownError('Failed to load statuses. Please try again.');
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
        setDropdownError('Failed to load form options. Please try again.');
      } finally {
        setLoadingDropdowns(false);
      }
    };

    fetchDropdownData();
  }, []);

  const fetchBidsWithCriteria = async (
    pageNum: number,
    pageSize: number,
    criteria: SearchCriteria = searchCriteria
  ) => {
    setLoading(true);
    try {
      // Construct query parameters with corrected parameter names
      const queryParams = new URLSearchParams({
        Page: (pageNum + 1).toString(),
        Size: pageSize.toString(),

        // Fixed parameter names according to your requirements
        ...(criteria?.categories?.length > 0 && {
          Categories: criteria?.categories.join(',')
        }),
        ...(criteria?.statuses?.length > 0 && {
          Status: criteria?.statuses.join(',')
        }),
        ...(criteria?.bidTypes?.length > 0 && {
          Type: criteria?.bidTypes.join(',')
        }),
        ...(criteria?.bidDeadlineFrom && {
          FromBidDeadLine: criteria?.bidDeadlineFrom.toISOString()
        }),
        ...(criteria?.bidDeadlineTo && {
          ToBidDeadLine: criteria?.bidDeadlineTo.toISOString()
        }),
        ...(criteria?.awardDateFrom && {
          FromAwardDate: criteria?.awardDateFrom.toISOString()
        }),
        ...(criteria?.awardDateTo && {
          ToAwardDate: criteria?.awardDateTo.toISOString()
        }),
        ...(criteria?.createdDateFrom && {
          FromCreateDate: criteria?.createdDateFrom.toISOString()
        }),
        ...(criteria?.createdDateTo && {
          ToCreateDate: criteria?.createdDateTo.toISOString()
        }),

        ...(criteria?.modifiedDate && {
          ModifiedDate: criteria?.modifiedDate.toISOString()
        }),
        ...(criteria?.title.trim() && {
          Title: encodeURIComponent(criteria?.title.trim())
        })
      });

      const response = (await httpRequest(
        'GET',
        `/bid_portal/bid_details/get?${queryParams}`
      )) as APIResponse;

      if (response.status === 'success') {
        // Transform the response data to match our component's expected structure
        const transformedBids: Bid[] = response?.data?.Data?.map((bid) => ({
          bidId: bid.BidID?.toString() || '',
          title: bid.Title,
          type: bid.Type,
          status: bid.Status,
          category:
            bid.Categories?.map((cat) => cat.CategoryName).join(', ') || '',
          uploads:
            (bid?.Documents?.filter(
              (doc) => doc.DocumentDescription === 'Bid Document'
            )?.length || 0) + (bid.ChildBidDocuments?.length || 0),
          downloads: bid.Downloads || '0',
          lastModified: bid.UpdateDate,
          modifiedBy: bid.UpdatedBy?.EmailID || '',
          contractNo: bid.ContractNo,
          goLiveDate: bid.GoLiveDate,
          bidDeadlineDate: bid.BidDeadLineDate,
          awardDate: bid.AwardDate,
          createDate: bid.CreateDate,
          updateDate: bid.UpdateDate,
          createdBy: bid.CreatedBy,
          updatedBy: bid.UpdatedBy,
          documents: bid.Documents || [],
          childBidDocuments: bid?.ChildBidDocuments || []
        }));

        setBids(transformedBids);
        setTotalCount(response.data.TotalRecords);
      }
    } catch (error) {
      console.error('Error fetching bids:', error);
      toast.error('Failed to fetch bids. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBids = async (pageNum: number, pageSize: number) => {
    await fetchBidsWithCriteria(pageNum, pageSize);
  };

  useEffect(() => {
    fetchBids(page, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleSearch = () => {
    setPage(0);
    fetchBidsWithCriteria(0, rowsPerPage);
  };

  const handleClear = () => {
    const clearedCriteria = {
      categories: [],
      statuses: [],
      bidTypes: [],
      bidDeadlineFrom: null,
      bidDeadlineTo: null,
      awardDateFrom: null,
      awardDateTo: null,
      createdDateFrom: null,
      createdDateTo: null,
      modifiedBy: '',
      modifiedDate: null,
      title: ''
    };
    setSearchCriteria(clearedCriteria);
    setPage(0);

    // Call fetchBids with the cleared criteria directly
    fetchBidsWithCriteria(0, rowsPerPage, clearedCriteria);
  };

  // Handle downloads click - open download details view with scroll to top
  const handleDownloadsClick = (bid: Bid) => {
    setDownloadDetailsState({
      isOpen: true,
      bidId: bid.bidId,
      bidDetails: {
        contractNo: bid.contractNo,
        title: bid.title,
        categories: bid.category
      }
    });
    // Scroll to top when opening download details
    scrollToTop();
  };

  // Handle uploads click - open uploaded files details view with scroll to top
  const handleUploadsClick = (bid: Bid) => {
    setUploadedFilesDetailsState({
      isOpen: true,
      bidId: bid.bidId,
      bidDetails: {
        contractNo: bid.contractNo,
        title: bid.title,
        categories: bid.category,
        documents: bid.documents, // Pass the documents array
        childBidDocuments: bid.childBidDocuments
      }
    });
    // Scroll to top when opening uploaded files details
    scrollToTop();
  };

  // Close download details view with scroll to top
  const handleCloseDownloadDetails = () => {
    setDownloadDetailsState({
      isOpen: false,
      bidId: '',
      bidDetails: {
        contractNo: '',
        title: '',
        categories: ''
      }
    });
    // Scroll to top when closing download details
    scrollToTop();
  };

  // Close uploaded files details view with scroll to top
  const handleCloseUploadedFilesDetails = () => {
    setUploadedFilesDetailsState({
      isOpen: false,
      bidId: '',
      bidDetails: {
        contractNo: '',
        title: '',
        categories: ''
      }
    });
    // Scroll to top when closing uploaded files details
    scrollToTop();
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Helper function to format date and time
  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (e) {
      return '-';
    }
  };

  // Get status color based on status text
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return theme.palette.success.main;
      case 'closed':
        return theme.palette.error.main;
      case 'pending':
        return theme.palette.warning.main;
      case 'awarded':
        return theme.palette.info.main;
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <>
      {/* Show download details if opened */}
      {downloadDetailsState.isOpen ? (
        <DownloadDetails
          bidId={downloadDetailsState.bidId}
          bidDetails={downloadDetailsState.bidDetails}
          onBack={handleCloseDownloadDetails}
        />
      ) : uploadedFilesDetailsState.isOpen ? (
        // Show uploaded files details if opened
        <UploadedFilesDetails
          bidId={uploadedFilesDetailsState.bidId}
          bidDetails={uploadedFilesDetailsState.bidDetails}
          onBack={handleCloseUploadedFilesDetails}
        />
      ) : (
        <Box
          sx={{
            p: 3,
            background: alpha(theme.palette.background.default, 0.8),
            minHeight: '100vh'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 4
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                position: 'relative',
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 100,
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: theme.palette.primary.main
                }
              }}
            >
              Bid Summary
            </Typography>
          </Box>

          {dropdownError && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
              }}
            >
              {dropdownError}
            </Alert>
          )}

          <Card
            sx={{
              mb: 4,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              overflow: 'hidden',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
            }}
          >
            <Box
              sx={{
                p: 2.5,
                background: `linear-gradient(to right, ${alpha(
                  theme.palette.primary.main,
                  0.05
                )}, ${alpha(theme.palette.primary.main, 0.02)})`,
                borderBottom: `1px solid ${alpha(
                  theme.palette.primary.main,
                  0.1
                )}`
              }}
            >
              <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Search Filters
              </Typography>
            </Box>
            <CardContent sx={{ p: 3 }}>
              {loadingDropdowns ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={2.5}>
                  {/* First row - Category, Type, Status */}
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.875rem',
                          transform: 'translate(14px, 11px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)'
                          },
                          '&.Mui-focused': {
                            fontWeight: 500
                          },
                          backgroundColor: 'white',
                          paddingLeft: '4px',
                          paddingRight: '4px',
                          zIndex: 1
                        }}
                      >
                        Category
                      </InputLabel>
                      <Select
                        open={openCategorySelect}
                        onOpen={() => setOpenCategorySelect(true)}
                        onClose={() => setOpenCategorySelect(false)}
                        multiple
                        value={searchCriteria.categories}
                        onChange={(e) => {
                          const selectedValues = e.target.value as any[];

                          // Handle "Select All" logic
                          if (selectedValues.includes('SELECT_ALL')) {
                            const allCategoryIDs = categories?.map(
                              (cat) => cat.CategoryID
                            );
                            const isAllSelected =
                              searchCriteria.categories?.length ===
                              categories?.length;

                            // If all categories are selected, deselect all; otherwise, select all
                            const finalCategories = isAllSelected
                              ? []
                              : allCategoryIDs;

                            setSearchCriteria({
                              ...searchCriteria,
                              categories: finalCategories
                            });
                          } else {
                            // Ensure no invalid "Unknown" category is included
                            const validSelectedValues = selectedValues.filter(
                              (value) =>
                                categories.some(
                                  (cat) => cat.CategoryID === value
                                )
                            );

                            setSearchCriteria({
                              ...searchCriteria,
                              categories: validSelectedValues
                            });
                          }
                        }}
                        sx={{
                          borderRadius: 1.5,
                          height: 40,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha(theme.palette.primary.main, 0.5)
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderWidth: 2
                          },
                          '& .MuiSelect-select': {
                            paddingRight: '32px !important'
                          }
                        }}
                        renderValue={(selected) => {
                          const selectedArray = selected as number[];
                          const totalSelected = selectedArray?.length;

                          if (totalSelected === 0) {
                            return (
                              <Box sx={{ color: 'text.secondary' }}>
                                Select categories
                              </Box>
                            );
                          }

                          return (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                width: '100%',
                                overflow: 'hidden',
                                position: 'relative'
                              }}
                            >
                              {/* Scrollable chips container */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 0.5,
                                  overflow: 'hidden',
                                  flexGrow: 1,
                                  maskImage:
                                    totalSelected > 1
                                      ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                      : 'none',
                                  WebkitMaskImage:
                                    totalSelected > 1
                                      ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                      : 'none'
                                }}
                              >
                                {selectedArray?.map((CategoryID) => {
                                  const categoryName =
                                    categories.find(
                                      (c) => c.CategoryID === CategoryID
                                    )?.CategoryName || 'Unknown';
                                  return (
                                    <Chip
                                      key={CategoryID}
                                      label={categoryName}
                                      size="small"
                                      sx={{
                                        height: 24,
                                        fontSize: '0.75rem',
                                        bgcolor: alpha(
                                          theme.palette.primary.main,
                                          0.1
                                        ),
                                        color: theme.palette.primary.main,
                                        fontWeight: 500,
                                        borderRadius: 1,
                                        flexShrink: 0,
                                        maxWidth: '120px',
                                        '& .MuiChip-label': {
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          paddingLeft: '8px',
                                          paddingRight: '8px'
                                        }
                                      }}
                                    />
                                  );
                                })}
                              </Box>

                              {/* Count badge - always visible on the right */}
                              {totalSelected > 3 && (
                                <Chip
                                  label={`${totalSelected}`}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.2
                                    ),
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                    borderRadius: 1,
                                    minWidth: '40px',
                                    flexShrink: 0,
                                    position: 'absolute',
                                    right: 0,
                                    backgroundColor: 'white',
                                    boxShadow:
                                      '-4px 0 8px rgba(255, 255, 255, 0.9)'
                                  }}
                                />
                              )}
                            </Box>
                          );
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 350,
                              borderRadius: 1.5,
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                              marginTop: 1
                            }
                          },
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left'
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left'
                          }
                        }}
                      >
                        {/* Select All Option */}
                        <MenuItem value="SELECT_ALL">
                          <Checkbox
                            checked={
                              searchCriteria.categories?.length ===
                              categories?.length
                            }
                            indeterminate={
                              searchCriteria.categories?.length > 0 &&
                              searchCriteria.categories?.length <
                                categories?.length
                            }
                          />
                          <ListItemText
                            primary="Select All"
                            sx={{ fontWeight: 'bold', color: 'primary.main' }}
                          />
                        </MenuItem>

                        {/* Individual Category Options */}
                        {categories?.map((category) => (
                          <MenuItem
                            key={category.CategoryID}
                            value={category.CategoryID}
                            sx={{
                              fontSize: '0.875rem',
                              '&.Mui-selected': {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.08
                                ),
                                '&:hover': {
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.12
                                  )
                                }
                              }
                            }}
                          >
                            <Checkbox
                              checked={
                                searchCriteria.categories.indexOf(
                                  category.CategoryID
                                ) > -1
                              }
                            />
                            {category.CategoryName}
                          </MenuItem>
                        ))}

                        <MenuItem
                          disableRipple
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            borderTop: '1px solid #ddd',
                            mt: 1,
                            pt: 1
                          }}
                        >
                          <button
                            style={{
                              backgroundColor: '#1976d2',
                              color: 'white',
                              padding: '5px 12px',
                              borderRadius: '6px',
                              border: 'none',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                            onClick={() => setOpenCategorySelect(false)}
                          >
                            Apply
                          </button>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.875rem',
                          transform: 'translate(14px, 11px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)'
                          },
                          '&.Mui-focused': {
                            fontWeight: 500
                          },
                          backgroundColor: 'white',
                          paddingLeft: '4px',
                          paddingRight: '4px',
                          zIndex: 1
                        }}
                      >
                        Status
                      </InputLabel>
                      <Select
                        open={openStatusSelect}
                        onOpen={() => setOpenStatusSelect(true)}
                        onClose={() => setOpenStatusSelect(false)}
                        multiple
                        value={searchCriteria.statuses}
                        onChange={(e) => {
                          const selectedValues = e.target.value as any[];

                          // Handle "Select All" logic
                          if (selectedValues.includes('SELECT_ALL')) {
                            const allStatusIDs = statuses?.map(
                              (status) => status.StatusID
                            );
                            const isAllSelected =
                              searchCriteria.statuses?.length ===
                              statuses?.length;

                            // If all statuses are selected, deselect all; otherwise, select all
                            const finalStatuses = isAllSelected
                              ? []
                              : allStatusIDs;

                            setSearchCriteria({
                              ...searchCriteria,
                              statuses: finalStatuses
                            });
                          } else {
                            // Ensure no invalid "Unknown" status is included
                            const validSelectedValues = selectedValues.filter(
                              (value) =>
                                statuses.some(
                                  (status) => status.StatusID === value
                                )
                            );

                            setSearchCriteria({
                              ...searchCriteria,
                              statuses: validSelectedValues
                            });
                          }
                        }}
                        sx={{
                          borderRadius: 1.5,
                          height: 40,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha(theme.palette.primary.main, 0.5)
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderWidth: 2
                          },
                          '& .MuiSelect-select': {
                            paddingRight: '32px !important'
                          }
                        }}
                        renderValue={(selected) => {
                          const selectedArray = selected as number[];
                          const totalSelected = selectedArray?.length;

                          if (totalSelected === 0) {
                            return (
                              <Box sx={{ color: 'text.secondary' }}>
                                Select statuses
                              </Box>
                            );
                          }

                          return (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                width: '100%',
                                overflow: 'hidden',
                                position: 'relative'
                              }}
                            >
                              {/* Scrollable chips container */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 0.5,
                                  overflow: 'hidden',
                                  flexGrow: 1,
                                  maskImage:
                                    totalSelected > 1
                                      ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                      : 'none',
                                  WebkitMaskImage:
                                    totalSelected > 1
                                      ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                      : 'none'
                                }}
                              >
                                {selectedArray?.map((statusId) => {
                                  const statusName =
                                    statuses.find(
                                      (s) => s.StatusID === statusId
                                    )?.Status || 'Unknown';
                                  return (
                                    <Chip
                                      key={statusId}
                                      label={statusName}
                                      size="small"
                                      sx={{
                                        height: 24,
                                        fontSize: '0.75rem',
                                        bgcolor: alpha(
                                          theme.palette.primary.main,
                                          0.1
                                        ),
                                        color: theme.palette.primary.main,
                                        fontWeight: 500,
                                        borderRadius: 1,
                                        flexShrink: 0,
                                        maxWidth: '120px',
                                        '& .MuiChip-label': {
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          paddingLeft: '8px',
                                          paddingRight: '8px'
                                        }
                                      }}
                                    />
                                  );
                                })}
                              </Box>

                              {/* Count badge - always visible on the right */}
                              {totalSelected > 3 && (
                                <Chip
                                  label={`${totalSelected}`}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.2
                                    ),
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                    borderRadius: 1,
                                    minWidth: '40px',
                                    flexShrink: 0,
                                    position: 'absolute',
                                    right: 0,
                                    backgroundColor: 'white',
                                    boxShadow:
                                      '-4px 0 8px rgba(255, 255, 255, 0.9)'
                                  }}
                                />
                              )}
                            </Box>
                          );
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 350,
                              borderRadius: 1.5,
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                              marginTop: 1
                            }
                          },
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left'
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left'
                          }
                        }}
                      >
                        {/* Select All Option */}
                        <MenuItem value="SELECT_ALL">
                          <Checkbox
                            checked={
                              searchCriteria.statuses?.length ===
                              statuses?.length
                            }
                            indeterminate={
                              searchCriteria.statuses?.length > 0 &&
                              searchCriteria.statuses?.length < statuses?.length
                            }
                          />
                          <ListItemText
                            primary="Select All"
                            sx={{ fontWeight: 'bold', color: 'primary.main' }}
                          />
                        </MenuItem>

                        {/* Individual Status Options */}
                        {statuses?.map((status) => (
                          <MenuItem
                            key={status.StatusID}
                            value={status.StatusID}
                            sx={{
                              fontSize: '0.875rem',
                              '&.Mui-selected': {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.08
                                ),
                                '&:hover': {
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.12
                                  )
                                }
                              }
                            }}
                          >
                            <Checkbox
                              checked={
                                searchCriteria.statuses.indexOf(
                                  status.StatusID
                                ) > -1
                              }
                            />
                            {status.Status}
                          </MenuItem>
                        ))}

                        <MenuItem
                          disableRipple
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            borderTop: '1px solid #ddd',
                            mt: 1,
                            pt: 1
                          }}
                        >
                          <button
                            style={{
                              backgroundColor: '#1976d2',
                              color: 'white',
                              padding: '5px 12px',
                              borderRadius: '6px',
                              border: 'none',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                            onClick={() => setOpenStatusSelect(false)}
                          >
                            Apply
                          </button>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth variant="outlined" size="small">
                      <InputLabel
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: '0.875rem',
                          transform: 'translate(14px, 11px) scale(1)',
                          '&.MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)'
                          },
                          '&.Mui-focused': {
                            fontWeight: 500
                          },
                          backgroundColor: 'white',
                          paddingLeft: '4px',
                          paddingRight: '4px',
                          zIndex: 1
                        }}
                      >
                        Type
                      </InputLabel>
                      <Select
                        open={openTypeSelect}
                        onOpen={() => setOpenTypeSelect(true)}
                        onClose={() => setOpenTypeSelect(false)}
                        multiple
                        value={searchCriteria.bidTypes}
                        onChange={(e) => {
                          const selectedValues = e.target.value as any[];

                          // Handle "Select All" logic
                          if (selectedValues.includes('SELECT_ALL')) {
                            const allTypeIDs = types?.map(
                              (type) => type.TypeID
                            );
                            const isAllSelected =
                              searchCriteria.bidTypes?.length === types?.length;

                            // If all types are selected, deselect all; otherwise, select all
                            const finalTypes = isAllSelected ? [] : allTypeIDs;

                            setSearchCriteria({
                              ...searchCriteria,
                              bidTypes: finalTypes
                            });
                          } else {
                            // Ensure no invalid "Unknown" type is included
                            const validSelectedValues = selectedValues.filter(
                              (value) =>
                                types.some((type) => type.TypeID === value)
                            );

                            setSearchCriteria({
                              ...searchCriteria,
                              bidTypes: validSelectedValues
                            });
                          }
                        }}
                        sx={{
                          borderRadius: 1.5,
                          height: 40,
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha(theme.palette.primary.main, 0.5)
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderWidth: 2
                          },
                          '& .MuiSelect-select': {
                            paddingRight: '32px !important'
                          }
                        }}
                        renderValue={(selected) => {
                          const selectedArray = selected as number[];
                          const totalSelected = selectedArray?.length;

                          if (totalSelected === 0) {
                            return (
                              <Box sx={{ color: 'text.secondary' }}>
                                Select types
                              </Box>
                            );
                          }

                          return (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                width: '100%',
                                overflow: 'hidden',
                                position: 'relative'
                              }}
                            >
                              {/* Scrollable chips container */}
                              <Box
                                sx={{
                                  display: 'flex',
                                  gap: 0.5,
                                  overflow: 'hidden',
                                  flexGrow: 1,
                                  maskImage:
                                    totalSelected > 1
                                      ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                      : 'none',
                                  WebkitMaskImage:
                                    totalSelected > 1
                                      ? 'linear-gradient(to right, black calc(100% - 60px), transparent 100%)'
                                      : 'none'
                                }}
                              >
                                {selectedArray?.map((TypeID) => {
                                  const TypeName =
                                    types.find((t) => t.TypeID === TypeID)
                                      ?.Type || 'Unknown';
                                  return (
                                    <Chip
                                      key={TypeID}
                                      label={TypeName}
                                      size="small"
                                      sx={{
                                        height: 24,
                                        fontSize: '0.75rem',
                                        bgcolor: alpha(
                                          theme.palette.primary.main,
                                          0.1
                                        ),
                                        color: theme.palette.primary.main,
                                        fontWeight: 500,
                                        borderRadius: 1,
                                        flexShrink: 0,
                                        maxWidth: '120px',
                                        '& .MuiChip-label': {
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis',
                                          whiteSpace: 'nowrap',
                                          paddingLeft: '8px',
                                          paddingRight: '8px'
                                        }
                                      }}
                                    />
                                  );
                                })}
                              </Box>

                              {/* Count badge - always visible on the right */}
                              {totalSelected > 3 && (
                                <Chip
                                  label={`${totalSelected}`}
                                  size="small"
                                  sx={{
                                    height: 24,
                                    fontSize: '0.75rem',
                                    bgcolor: alpha(
                                      theme.palette.primary.main,
                                      0.2
                                    ),
                                    color: theme.palette.primary.main,
                                    fontWeight: 600,
                                    borderRadius: 1,
                                    minWidth: '40px',
                                    flexShrink: 0,
                                    position: 'absolute',
                                    right: 0,
                                    backgroundColor: 'white',
                                    boxShadow:
                                      '-4px 0 8px rgba(255, 255, 255, 0.9)'
                                  }}
                                />
                              )}
                            </Box>
                          );
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 350,
                              borderRadius: 1.5,
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                              marginTop: 1
                            }
                          },
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left'
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left'
                          }
                        }}
                      >
                        {/* Select All Option */}
                        <MenuItem value="SELECT_ALL">
                          <Checkbox
                            checked={
                              searchCriteria.bidTypes?.length === types?.length
                            }
                            indeterminate={
                              searchCriteria.bidTypes?.length > 0 &&
                              searchCriteria.bidTypes?.length < types?.length
                            }
                          />
                          <ListItemText
                            primary="Select All"
                            sx={{ fontWeight: 'bold', color: 'primary.main' }}
                          />
                        </MenuItem>

                        {/* Individual Type Options */}
                        {types?.map((type) => (
                          <MenuItem
                            key={type.TypeID}
                            value={type.TypeID}
                            sx={{
                              fontSize: '0.875rem',
                              '&.Mui-selected': {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.08
                                ),
                                '&:hover': {
                                  backgroundColor: alpha(
                                    theme.palette.primary.main,
                                    0.12
                                  )
                                }
                              }
                            }}
                          >
                            <Checkbox
                              checked={
                                searchCriteria.bidTypes.indexOf(type.TypeID) >
                                -1
                              }
                            />
                            {type.Type}
                          </MenuItem>
                        ))}

                        <MenuItem
                          disableRipple
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            borderTop: '1px solid #ddd',
                            mt: 1,
                            pt: 1
                          }}
                        >
                          <button
                            style={{
                              backgroundColor: '#1976d2',
                              color: 'white',
                              padding: '5px 12px',
                              borderRadius: '6px',
                              border: 'none',
                              cursor: 'pointer',
                              fontWeight: '600'
                            }}
                            onClick={() => setOpenTypeSelect(false)}
                          >
                            Apply
                          </button>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Second row - Bid Deadline and Award Date Ranges */}
                  {/* <Grid item xs={12} sm={6} md={6}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}
                    >
                      Bid Deadline
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <DatePicker
                          label="From"
                          value={searchCriteria.bidDeadlineFrom}
                          onChange={(date) =>
                            setSearchCriteria({
                              ...searchCriteria,
                              bidDeadlineFrom: date
                            })
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small',
                              variant: 'outlined',
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  height: 40
                                }
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <DatePicker
                          label="To"
                          value={searchCriteria.bidDeadlineTo}
                          onChange={(date) =>
                            setSearchCriteria({
                              ...searchCriteria,
                              bidDeadlineTo: date
                            })
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small',
                              variant: 'outlined',
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  height: 40
                                }
                              }
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={6} md={6}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}
                    >
                      Award Date
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <DatePicker
                          label="From"
                          value={searchCriteria.awardDateFrom}
                          onChange={(date) =>
                            setSearchCriteria({
                              ...searchCriteria,
                              awardDateFrom: date
                            })
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small',
                              variant: 'outlined',
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  height: 40
                                }
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <DatePicker
                          label="To"
                          value={searchCriteria.awardDateTo}
                          onChange={(date) =>
                            setSearchCriteria({
                              ...searchCriteria,
                              awardDateTo: date
                            })
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small',
                              variant: 'outlined',
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  height: 40
                                }
                              }
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid> */}

                  {/* Third row - Create Date Range and Modified Date (single) */}
                  <Grid item xs={12} sm={6} md={6}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 1, fontWeight: 600 }}
                    >
                      Create Date
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <DatePicker
                          label="From"
                          value={searchCriteria.createdDateFrom}
                          onChange={(date) =>
                            setSearchCriteria({
                              ...searchCriteria,
                              createdDateFrom: date
                            })
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small',
                              variant: 'outlined',
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  height: 40
                                }
                              }
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <DatePicker
                          label="To"
                          value={searchCriteria.createdDateTo}
                          onChange={(date) =>
                            setSearchCriteria({
                              ...searchCriteria,
                              createdDateTo: date
                            })
                          }
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small',
                              variant: 'outlined',
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 1.5,
                                  height: 40
                                }
                              }
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid item xs={12} sm={3} md={3} mt={3.5}>
                    <DatePicker
                      label="Modified Date"
                      value={searchCriteria.modifiedDate}
                      onChange={(date) =>
                        setSearchCriteria({
                          ...searchCriteria,
                          modifiedDate: date
                        })
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'small',
                          variant: 'outlined',
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              height: 40
                            }
                          }
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3} md={3} mt={3.5}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Title"
                      variant="outlined"
                      value={searchCriteria.title}
                      onChange={(e) =>
                        setSearchCriteria({
                          ...searchCriteria,
                          title: e.target.value
                        })
                      }
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          height: 40
                        }
                      }}
                    />
                  </Grid>

                  {/* Search and Clear buttons - new row */}
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 2,
                        justifyContent: 'flex-end',
                        mt: 1
                      }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSearch}
                        startIcon={<SearchIcon />}
                        size="small"
                        sx={{
                          borderRadius: 1.5,
                          px: 3,
                          py: 0.8,
                          fontWeight: 'bold',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.18)',
                            transform: 'translateY(-2px)'
                          }
                        }}
                      >
                        Search
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleClear}
                        startIcon={<ClearAll />}
                        size="small"
                        sx={{
                          borderRadius: 1.5,
                          px: 3,
                          py: 0.8,
                          fontWeight: 'bold',
                          borderWidth: '1px',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderWidth: '1px',
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.04
                            )
                          }
                        }}
                      >
                        Clear
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              )}
            </CardContent>
          </Card>

          <Paper
            sx={{
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
            }}
          >
            <Box
              sx={{
                p: 2.5,
                bgcolor: `linear-gradient(to right, ${alpha(
                  theme.palette.primary.main,
                  0.05
                )}, ${alpha(theme.palette.primary.main, 0.02)})`,
                borderBottom: `1px solid ${alpha(
                  theme.palette.primary.main,
                  0.1
                )}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                Bids
              </Typography>
              {!loading && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontWeight: 500 }}
                >
                  Total Results: {totalCount}
                </Typography>
              )}
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table
                    sx={{
                      '& .MuiTableCell-head': {
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        fontWeight: 'bold',
                        color: theme.palette.text.primary,
                        py: 2
                      },
                      '& .MuiTableCell-body': {
                        py: 1.8
                      }
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="center">Uploads</TableCell>
                        <TableCell align="center">Downloads</TableCell>
                        <TableCell>Last Modified</TableCell>
                        <TableCell>Modified By</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {bids?.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: 2
                              }}
                            >
                              <Warning
                                sx={{
                                  fontSize: 40,
                                  color: alpha(
                                    theme.palette.text.secondary,
                                    0.6
                                  )
                                }}
                              />
                              <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ fontWeight: 500 }}
                              >
                                No bids found. Try adjusting your search
                                criteria.
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : (
                        bids?.map((bid) => (
                          <TableRow
                            key={bid.bidId}
                            hover
                            sx={{
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.03)
                              },
                              transition: 'background-color 0.2s ease',
                              '&:nth-of-type(even)': {
                                bgcolor: alpha(
                                  theme.palette.background.default,
                                  0.4
                                )
                              }
                            }}
                          >
                            <TableCell
                              sx={{
                                fontWeight: 500,
                                maxWidth: 250,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {bid.title}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={bid.type}
                                size="small"
                                sx={{
                                  bgcolor: alpha(theme.palette.info.main, 0.1),
                                  color: theme.palette.info.main,
                                  fontWeight: 500,
                                  borderRadius: 1
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={bid.status}
                                size="small"
                                sx={{
                                  bgcolor: alpha(
                                    getStatusColor(bid.status),
                                    0.1
                                  ),
                                  color: getStatusColor(bid.status),
                                  fontWeight: 'bold',
                                  borderRadius: 1
                                }}
                              />
                            </TableCell>
                            <Tooltip title={bid.category} arrow placement="top">
                              <TableCell
                                sx={{
                                  maxWidth: 200,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {bid.category as any}
                              </TableCell>
                            </Tooltip>

                            <TableCell align="center" sx={{ fontWeight: 500 }}>
                              <Link
                                component="button"
                                variant="body2"
                                onClick={() => handleUploadsClick(bid)}
                                sx={{
                                  color: theme.palette.primary.main,
                                  fontWeight: 'bold',
                                  textDecoration: 'none',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                    color: theme.palette.primary.dark
                                  }
                                }}
                              >
                                {bid.uploads}
                              </Link>
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 500 }}>
                              <Link
                                component="button"
                                variant="body2"
                                onClick={() => handleDownloadsClick(bid)}
                                sx={{
                                  color: theme.palette.primary.main,
                                  fontWeight: 'bold',
                                  textDecoration: 'none',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    textDecoration: 'underline',
                                    color: theme.palette.primary.dark
                                  }
                                }}
                              >
                                {bid.downloads}
                              </Link>
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.875rem' }}>
                              {formatDateTime(bid.lastModified)}
                            </TableCell>
                            <TableCell
                              sx={{
                                maxWidth: 180,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '0.875rem'
                              }}
                            >
                              <Tooltip
                                title={bid.modifiedBy}
                                arrow
                                placement="top"
                              >
                                {bid.modifiedBy as any}
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box
                  sx={{
                    borderTop: `1px solid ${alpha(
                      theme.palette.primary.main,
                      0.1
                    )}`,
                    bgcolor: alpha(theme.palette.background.paper, 0.5)
                  }}
                >
                  <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{
                      '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                        {
                          fontWeight: 500
                        },
                      '& .MuiTablePagination-select': {
                        borderRadius: 1.5,
                        '&:focus': {
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.05
                          )
                        }
                      },
                      '& .MuiTablePagination-actions': {
                        '& .MuiIconButton-root': {
                          '&:hover': {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.1
                            )
                          }
                        }
                      }
                    }}
                  />
                </Box>
              </>
            )}
          </Paper>
        </Box>
      )}
    </>
  );
};

export default BidSummary;
